import { isoWeek, periodicTitle, type PeriodicSettings } from './periodicNotes';

export interface CalendarDay {
  date: Date;
  /** False for the leading/trailing days that pad the grid to whole weeks. */
  inMonth: boolean;
}

export interface CalendarWeek {
  /** The ISO week, labelled by its Thursday — the same rule the weekly note uses. */
  week: number;
  /** Any day of the week; used to open that week's note. */
  monday: Date;
  days: CalendarDay[];
}

/**
 * The weeks of a month as a Monday-first grid, padded with neighbouring days.
 *
 * Monday-first because the week column is the ISO week, and ISO weeks start on
 * Monday: a Sunday-first row would put two different week numbers in one row.
 */
export function monthGrid(year: number, month: number): CalendarWeek[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const offset = (first.getDay() + 6) % 7; // days since Monday
  const start = new Date(year, month, 1 - offset);
  const weeks: CalendarWeek[] = [];
  for (let w = 0; w < 6; w++) {
    const monday = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7);
    if (monday > last) break;
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
      return { date, inMonth: date.getMonth() === month };
    });
    weeks.push({ week: isoWeek(monday).week, monday, days });
  }
  return weeks;
}

/** Whether a periodic note for `date` already exists, by the same title rule "open today" uses. */
export function hasNote(
  titles: ReadonlySet<string>,
  date: Date,
  settings: PeriodicSettings,
): boolean {
  return titles.has(periodicTitle(date, settings).toLowerCase());
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
